import React, { useState, useRef, useEffect } from "react";
import {
  Camera,
  CameraOff,
  Mic,
  MicOff,
  Phone,
  Maximize,
  Minimize,
} from "lucide-react";
import Text from "../Text";
import Messages from "../../Shared/Messages";
import { getInitials } from "../../utils/strings";

const styles = {
  container: {
    position: "relative",
    background:
      "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 50%, #000000 100%)",
    overflow: "hidden",
    transition: "all 0.5s ease",
    width: "100%",
    height: "100vh",
    // Prevent pull-to-refresh
    overscrollBehavior: "none",
    touchAction: "manipulation",
    userSelect: "none",
    WebkitUserSelect: "none",
    WebkitTouchCallout: "none",
  },
  containerFullscreen: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 50,
  },
  remoteVideoContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  remoteVideo: {
    width: "100%",
    height: "100%",
    backgroundColor: "#1a1a1a",
    borderRadius: "8px",
    overflow: "hidden",
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  gradientOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      "linear-gradient(to top, rgba(0,0,0,0.2) 0%, transparent 50%, rgba(0,0,0,0.2) 100%)",
    pointerEvents: "none",
  },
  pipContainer: {
    position: "absolute",
    zIndex: 20,
    transition: "all 0.3s ease",
    width: "240px",
    height: "135px",
  },
  pipContainerHover: {
    transform: "scale(1.05)",
  },
  pipContainerDragging: {
    cursor: "grabbing",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.8)",
  },
  pipVideoContainer: {
    position: "relative",
    width: "100%",
    height: "100%",
    borderRadius: "8px",
    overflow: "hidden",
    border: "2px solid rgba(255, 255, 255, 0.2)",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.8)",
    backdropFilter: "blur(4px)",
  },
  pipVideo: {
    width: "100%",
    height: "100%",
    backgroundColor: "#2d2d2d",
    position: "relative",
  },
  recordingIndicator: {
    position: "absolute",
    top: "8px",
    right: "8px",
    width: "8px",
    height: "8px",
    backgroundColor: "#ef4444",
    borderRadius: "50%",
    animation: "pulse 2s infinite",
  },
  topControls: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 30,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "24px",
    transition: "all 0.3s ease",
  },
  topControlsHidden: {
    opacity: 0,
    transform: "translateY(-16px)",
  },
  topControlsVisible: {
    opacity: 1,
    transform: "translateY(0)",
  },
  leftControls: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  timer: {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    backdropFilter: "blur(12px)",
    borderRadius: "9999px",
    padding: "8px 16px",
    color: "white",
    fontSize: "14px",
    fontFamily: "monospace",
  },
  statusBadge: {
    backgroundColor: "rgba(34, 197, 94, 0.2)",
    backdropFilter: "blur(12px)",
    borderRadius: "9999px",
    padding: "4px 12px",
    color: "#4ade80",
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  statusDot: {
    width: "8px",
    height: "8px",
    backgroundColor: "#4ade80",
    borderRadius: "50%",
    animation: "pulse 2s infinite",
  },
  fullscreenButton: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "rgba(31, 41, 55, 0.8)",
    color: "white",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.3s ease",
    backdropFilter: "blur(4px)",
    cursor: "pointer",
  },
  bottomControls: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 30,
    display: "flex",
    justifyContent: "center",
    padding: "24px",
    transition: "all 0.3s ease",
  },
  bottomControlsHidden: {
    opacity: 0,
    transform: "translateY(16px)",
  },
  bottomControlsVisible: {
    opacity: 1,
    transform: "translateY(0)",
  },
  controlsPanel: {
    backgroundColor: "rgba(17, 24, 39, 0.8)",
    backdropFilter: "blur(12px)",
    borderRadius: "16px",
    padding: "16px",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.8)",
    border: "1px solid rgba(75, 85, 99, 0.5)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
  },
  videoActions: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  actionButton: {
    width: "48px",
    height: "48px",
    borderRadius: "50%",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.3s ease",
    cursor: "pointer",
  },
  actionButtonNormal: {
    backgroundColor: "rgba(31, 41, 55, 0.8)",
    color: "white",
    backdropFilter: "blur(4px)",
  },
  actionButtonActive: {
    backgroundColor: "#ef4444",
    color: "white",
    boxShadow: "0 0 20px rgba(239, 68, 68, 0.3)",
  },
  hangupButton: {
    backgroundColor: "#ef4444",
    color: "white",
    boxShadow: "0 0 20px rgba(239, 68, 68, 0.3)",
  },
  videoOffOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 10,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    backgroundColor: "#1f2937",
  },
  videoOffAvatar: {
    width: "88px",
    height: "88px",
    borderRadius: "50%",
    backgroundColor: "rgba(255, 255, 255, 0.12)",
    color: "white",
    fontSize: "30px",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    letterSpacing: "1px",
  },
  videoOffName: {
    color: "rgba(255, 255, 255, 0.85)",
    fontSize: "16px",
  },
  videoOffAvatarPip: {
    width: "40px",
    height: "40px",
    fontSize: "14px",
  },
};

const CallTimer = ({ setCallDuration, callEnded, bare }) => {
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (callEnded) return;

    const interval = setInterval(() => {
      setDuration((prev) => {
        const newDuration = prev + 1;
        if (setCallDuration) setCallDuration(newDuration);
        return newDuration;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [callEnded, setCallDuration]);

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hrs > 0) {
      return `${hrs.toString().padStart(2, "0")}:${mins
        .toString()
        .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  if (bare) {
    return formatTime(duration);
  }
  return <div style={styles.timer}>{formatTime(duration)}</div>;
};

const VideoActions = ({ leaveRoom, activeRoom, videoOff, setVideoOff }) => {
  const [muted, setMuted] = useState(false);

  const toggleMute = () => {
    if (activeRoom) {
      activeRoom.localParticipant.audioTracks.forEach((audioTrack) => {
        if (muted) {
          audioTrack.track.enable();
        } else {
          audioTrack.track.disable();
        }
      });
    }
    setMuted(!muted);
  };

  const toggleVideo = () => {
    if (activeRoom) {
      activeRoom.localParticipant.videoTracks.forEach((videoTrack) => {
        if (videoOff) {
          videoTrack.track.enable();
        } else {
          videoTrack.track.disable();
        }
      });
    }
    setVideoOff(!videoOff);
  };

  const handleButtonHover = (e) => {
    e.target.style.transform = "scale(1.1)";
  };

  const handleButtonLeave = (e) => {
    e.target.style.transform = "scale(1)";
  };

  const handleButtonDown = (e) => {
    e.target.style.transform = "scale(0.95)";
  };

  const handleButtonUp = (e) => {
    e.target.style.transform = "scale(1.1)";
  };

  return (
    <div style={styles.videoActions}>
      <button
        onClick={toggleMute}
        style={{
          ...styles.actionButton,
          ...(muted ? styles.actionButtonActive : styles.actionButtonNormal),
        }}
        onMouseEnter={handleButtonHover}
        onMouseLeave={handleButtonLeave}
        onMouseDown={handleButtonDown}
        onMouseUp={handleButtonUp}
      >
        {muted ? <MicOff size={20} /> : <Mic size={20} />}
      </button>

      <button
        onClick={toggleVideo}
        style={{
          ...styles.actionButton,
          ...(videoOff ? styles.actionButtonActive : styles.actionButtonNormal),
        }}
        onMouseEnter={handleButtonHover}
        onMouseLeave={handleButtonLeave}
        onMouseDown={handleButtonDown}
        onMouseUp={handleButtonUp}
      >
        {videoOff ? <CameraOff size={20} /> : <Camera size={20} />}
      </button>

      <button
        onClick={leaveRoom}
        style={{
          ...styles.actionButton,
          ...styles.hangupButton,
        }}
        onMouseEnter={handleButtonHover}
        onMouseLeave={handleButtonLeave}
        onMouseDown={handleButtonDown}
        onMouseUp={handleButtonUp}
      >
        <Phone size={20} style={{ transform: "rotate(135deg)" }} />
      </button>
    </div>
  );
};

const VideoRoom = ({
  localVideo,
  setRemoteMedia,
  setLocalMedia,
  setCallDuration,
  callEnded,
  leaveRoom,
  activeRoom,
  embedded,
  transcribing,
  otherParticipantName,
  localParticipantName,
  remoteVideoOff,
}) => {
  const [localVideoOff, setLocalVideoOff] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [pipPosition, setPipPosition] = useState({ x: 20, y: 20 });
  const [showControls, setShowControls] = useState(true);
  const [isHoveringPip, setIsHoveringPip] = useState(false);
  const pipRef = useRef(null);
  const dragRef = useRef({ offsetX: 0, offsetY: 0 });
  const controlsTimeoutRef = useRef(null);
  const containerRef = useRef(null);

  // In the mini viewer, start the local PiP bottom-left so it doesn't sit
  // under the compact top controls (it stays draggable either way).
  useEffect(() => {
    if (!embedded || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const pipHeight = 72;
    setPipPosition({
      x: 12,
      y: Math.max(12, rect.height - pipHeight - 96),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded]);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 3000);
  };

  useEffect(() => {
    handleMouseMove();

    if (embedded) {
      // Embedded in the in-app mini viewer: the rest of the app must keep
      // scrolling, so skip the document-level gesture blockers below.
      return () => {
        if (controlsTimeoutRef.current) {
          clearTimeout(controlsTimeoutRef.current);
        }
      };
    }

    // Prevent pull-to-refresh and other mobile gestures that could kill the call
    const preventRefresh = (e) => {
      // Prevent pull-to-refresh
      if (e.touches.length === 1 && e.touches[0].clientY <= 50) {
        e.preventDefault();
      }
    };

    const preventScroll = (e) => {
      e.preventDefault();
    };

    // Add event listeners to prevent accidental refresh
    document.addEventListener("touchstart", preventRefresh, { passive: false });
    document.addEventListener("touchmove", preventScroll, { passive: false });
    document.addEventListener("wheel", preventScroll, { passive: false });

    // Prevent browser back/forward gestures
    window.addEventListener("beforeunload", (e) => {
      e.preventDefault();
      e.returnValue = "";
    });

    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      // Clean up event listeners
      document.removeEventListener("touchstart", preventRefresh);
      document.removeEventListener("touchmove", preventScroll);
      document.removeEventListener("wheel", preventScroll);
      window.removeEventListener("beforeunload", (e) => {
        e.preventDefault();
        e.returnValue = "";
      });
    };
  }, [embedded]);

  const handlePipMouseDown = (e) => {
    setIsDragging(true);
    const rect = pipRef.current.getBoundingClientRect();
    dragRef.current = {
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
    };
  };

  const handleMouseMoveGlobal = (e) => {
    if (isDragging && pipRef.current) {
      const container = pipRef.current.parentElement;
      const containerRect = container.getBoundingClientRect();
      const pipRect = pipRef.current.getBoundingClientRect();

      let newX = e.clientX - containerRect.left - dragRef.current.offsetX;
      let newY = e.clientY - containerRect.top - dragRef.current.offsetY;

      newX = Math.max(0, Math.min(newX, containerRect.width - pipRect.width));
      newY = Math.max(0, Math.min(newY, containerRect.height - pipRect.height));

      setPipPosition({ x: newX, y: newY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMoveGlobal);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMoveGlobal);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging]);

  const containerStyle = {
    ...styles.container,
    ...(embedded && !isFullscreen ? { height: "100%" } : {}),
    ...(isFullscreen ? styles.containerFullscreen : {}),
  };

  const pipContainerStyle = {
    ...styles.pipContainer,
    ...(embedded && !isFullscreen ? { width: "128px", height: "72px" } : {}),
    left: `${pipPosition.x}px`,
    top: `${pipPosition.y}px`,
    cursor: isDragging ? "grabbing" : "grab",
    ...(isHoveringPip ? styles.pipContainerHover : {}),
    ...(isDragging ? styles.pipContainerDragging : {}),
  };

  const compact = embedded && !isFullscreen;

  const topControlsStyle = {
    ...styles.topControls,
    ...(compact ? { padding: "10px 12px" } : {}),
    ...(showControls ? styles.topControlsVisible : styles.topControlsHidden),
  };

  const timerBadgeStyle = compact
    ? { ...styles.timer, padding: "4px 10px", fontSize: "12px" }
    : styles.timer;

  const statusBadgeStyle = compact
    ? {
        ...styles.statusBadge,
        padding: "3px 8px",
        fontSize: "11px",
        gap: "5px",
      }
    : styles.statusBadge;

  const leftControlsStyle = compact
    ? { ...styles.leftControls, gap: "8px", flexWrap: "wrap" }
    : styles.leftControls;

  const fullscreenButtonStyle = compact
    ? { ...styles.fullscreenButton, width: "32px", height: "32px" }
    : styles.fullscreenButton;

  const bottomControlsStyle = {
    ...styles.bottomControls,
    ...(showControls
      ? styles.bottomControlsVisible
      : styles.bottomControlsHidden),
  };

  return (
    <>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        
        /* Smooth video size transitions + Mobile responsive */
        .video-room-container video {
          transition: all 0.8s ease-out;
          object-fit: cover;
          width: 100% !important;
          height: 100% !important;
          position: absolute;
          top: 0;
          left: 0;
        }

        /* Mobile-specific optimizations */
        @media (max-width: 768px) {
          .video-room-container video {
            object-fit: cover;
          }
        }

        /* Portrait orientation - common for mobile patients */
        @media (orientation: portrait) {
          .video-room-container video {
            object-fit: cover;
          }
        }

        /* Landscape orientation */
        @media (orientation: landscape) {
          .video-room-container video {
            object-fit: contain;
          }
        }
      `}</style>

      <div
        ref={containerRef}
        className="video-room-container"
        style={containerStyle}
        onMouseMove={handleMouseMove}
        onTouchStart={() => handleMouseMove()}
        onTouchMove={() => handleMouseMove()}
      >
        {/* Main Remote Video - Full Screen */}
        <div style={styles.remoteVideoContainer}>
          <div style={styles.remoteVideo} ref={setRemoteMedia} />
          {remoteVideoOff && (
            <div style={styles.videoOffOverlay}>
              <div style={styles.videoOffAvatar}>
                {getInitials(otherParticipantName)}
              </div>
              {otherParticipantName && (
                <div style={styles.videoOffName}>{otherParticipantName}</div>
              )}
            </div>
          )}
          <div style={styles.gradientOverlay} />
        </div>

        {/* Picture-in-Picture Local Video - Always show for debugging */}
        <div
          ref={pipRef}
          style={pipContainerStyle}
          onMouseDown={handlePipMouseDown}
          onMouseEnter={() => setIsHoveringPip(true)}
          onMouseLeave={() => setIsHoveringPip(false)}
        >
          <div style={styles.pipVideoContainer}>
            <div style={styles.pipVideo} ref={setLocalMedia} />
            {localVideoOff && (
              <div style={styles.videoOffOverlay}>
                <div
                  style={{
                    ...styles.videoOffAvatar,
                    ...styles.videoOffAvatarPip,
                  }}
                >
                  {getInitials(localParticipantName)}
                </div>
              </div>
            )}
            <div style={styles.recordingIndicator} />
          </div>
        </div>

        {/* Top Controls Bar */}
        <div style={topControlsStyle}>
          <div style={leftControlsStyle}>
            <div style={timerBadgeStyle}>
              <CallTimer
                setCallDuration={setCallDuration}
                callEnded={callEnded}
                bare
              />
            </div>
            <div style={statusBadgeStyle}>
              <div style={styles.statusDot} />
              <Text content={Messages.video_call_connected} />
            </div>
            {transcribing && (
              <div style={statusBadgeStyle}>
                <Mic size={12} />
                <Text content={Messages.video_call_transcribing} />
              </div>
            )}
          </div>

          <button
            onClick={toggleFullscreen}
            style={fullscreenButtonStyle}
            onMouseEnter={(e) =>
              (e.target.style.backgroundColor = "rgba(55, 65, 81, 0.8)")
            }
            onMouseLeave={(e) =>
              (e.target.style.backgroundColor = "rgba(31, 41, 55, 0.8)")
            }
          >
            {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
          </button>
        </div>

        {/* Bottom Controls Bar */}
        <div style={bottomControlsStyle}>
          <div style={styles.controlsPanel}>
            <VideoActions
              leaveRoom={leaveRoom}
              activeRoom={activeRoom}
              videoOff={localVideoOff}
              setVideoOff={setLocalVideoOff}
            />
          </div>
        </div>

      </div>
    </>
  );
};

export default VideoRoom;
